import { useCallback, useEffect, useState } from 'react'
import type { UseFormReturnType } from '@mantine/form'
import type { Price } from 'api/dto/price'
import type { RegisteredVisitor } from '../types'

interface UseVisitorRegistrationFormProps {
  form: UseFormReturnType<RegisteredVisitor, (values: RegisteredVisitor) => RegisteredVisitor>
  prices: Price[] | undefined
  isLoadingPrices: boolean
  isSuccess: boolean
}

export function useVisitorRegistrationForm({ form, prices, isLoadingPrices, isSuccess }: UseVisitorRegistrationFormProps) {
  const [showCompanion, setShowCompanion] = useState(false)

  // Auto-select first available price
  useEffect(() => {
    if (isLoadingPrices || !isSuccess) {
      return
    }

    if (prices?.length && form.values.price === '') {
      const price = prices.find((price) => !price.isSoldOut)

      if (price) {
        form.setFieldValue('price', price.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices, isSuccess, isLoadingPrices])

  const handleQuantityChange = useCallback(
    (numTickets: number) => {
      form.setFieldValue('numberOfTickets', numTickets)

      const currentAdditional = form.values.additionalVisitors || []
      const newLength = numTickets - 1

      // Find the default price (first non-sold-out ticket)
      const defaultPrice = prices?.find((p) => !p.isSoldOut)?.id || ''

      if (newLength > currentAdditional.length) {
        const toAdd = newLength - currentAdditional.length
        for (let i = 0; i < toAdd; i++) {
          form.insertListItem('additionalVisitors', {
            firstName: '',
            lastName: '',
            email: '',
            companyName: '',
            companyPhone: '',
            cellphone: '',
            hasConsent: false,
            dietaryRestrictions: '',
            price: defaultPrice,
            addons: [],
            address: {
              street: '',
              city: '',
              province: '',
              postalCode: '',
              country: '',
            },
          })
        }
      } else if (newLength < currentAdditional.length) {
        const toKeep = currentAdditional.slice(0, newLength)
        form.setFieldValue('additionalVisitors', toKeep)
      }
    },
    [form, prices],
  )

  const handleMainAddonToggle = useCallback(
    (addonId: string, isCompanion = false) => {
      const currentAddons = form.values.addons || []
      const isCurrentlySelected = currentAddons.includes(addonId)

      if (isCurrentlySelected) {
        form.setFieldValue(
          'addons',
          currentAddons.filter((id) => id !== addonId),
        )
        if (isCompanion) {
          form.removeListItem('companions', 0)
          setShowCompanion(false)
        }
      } else {
        form.setFieldValue('addons', [...currentAddons, addonId])
        if (isCompanion) {
          form.insertListItem('companions', { firstName: '', lastName: '' }, 0)
          setShowCompanion(true)
        }
      }
    },
    [form],
  )

  const handleAttendeeAddonToggle = useCallback(
    (index: number, addonId: string) => {
      const currentAddons = form.values.additionalVisitors[index]?.addons || []
      const isCurrentlySelected = currentAddons.includes(addonId)

      if (isCurrentlySelected) {
        form.setFieldValue(
          `additionalVisitors.${index}.addons`,
          currentAddons.filter((id) => id !== addonId),
        )
      } else {
        form.setFieldValue(`additionalVisitors.${index}.addons`, [...currentAddons, addonId])
      }
    },
    [form],
  )

  return {
    showCompanion,
    handleQuantityChange,
    handleMainAddonToggle,
    handleAttendeeAddonToggle,
  }
}
